/**
 * NDD Countdown Widget — Decorating Centre Online
 * DCOCSP-171
 *
 * Custom Element: <ndd-countdown>
 *
 * Behaviour:
 *  - Before cutoff (weekday): live countdown + estimated delivery date
 *  - After cutoff (weekday): widget is hidden (reappears at midnight)
 *  - Weekend: static "Order now, delivered by [date]" message
 *  - Dropship products: alternative supplier-delivery message
 *
 * Delivery date rules (DCO):
 *  - Mon–Thu before cutoff → same-day dispatch → next-day delivery
 *  - Mon–Thu after cutoff  → next-day dispatch → day-after delivery
 *  - Friday before cutoff  → Friday dispatch   → Saturday delivery
 *  - Friday after cutoff   → Monday dispatch   → Tuesday delivery
 *  - Saturday / Sunday     → Monday dispatch   → Tuesday delivery
 *  - Sundays always skipped; excluded dates (bank holidays) skipped
 */

// ── UK time helpers (Europe/London via IANA) ──────────────────────────────────

/**
 * Returns a Date whose UTC-based getters reflect UK local time (GMT or BST),
 * delegating DST awareness to the browser's IANA timezone database.
 *
 * Time override priority (highest → lowest):
 *  1. `?ndd_test=YYYY-MM-DDTHH:MM` URL param — for Playwright / automated testing
 *  2. Customiser testMode + testDatetime block settings — for manual QA in the editor
 *  3. Real current time via Intl.DateTimeFormat('Europe/London')
 */
const getUKNow = (testMode, testDatetime, testOffset = 0) => {
  // 1. URL param: ?ndd_test=2026-04-18T13:30 — enables time-based Playwright tests
  //    without requiring Customiser changes between test cases.
  const urlParam = new URLSearchParams(window.location.search).get('ndd_test');
  if (urlParam) {
    const withSeconds = urlParam.length === 16 ? `${urlParam}:00Z` : `${urlParam}Z`;
    const parsed = new Date(withSeconds);
    if (!isNaN(parsed.getTime())) return parsed;
  }

  // 2. Customiser test mode (manual QA override via block settings).
  if (testMode && testDatetime) {
    // The user types UK local time (e.g. "2026-04-20T13:30").
    // Append 'Z' to force a UTC parse — that way getUTCHours() / getUTCDay()
    // return exactly the values the user intended without any offset being added
    // on top (the rest of this module treats UTC getters as UK-local getters).
    const withSeconds = testDatetime.length === 16 ? `${testDatetime}:00Z` : `${testDatetime}Z`;
    const parsed = new Date(withSeconds);
    if (!isNaN(parsed.getTime())) return new Date(parsed.getTime() + testOffset);
  }

  // 3. Real current UK time via IANA timezone database.
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(now);

  const get = (type) => parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);

  return new Date(
    Date.UTC(
      get('year'),
      get('month') - 1, // Intl returns 1–12; Date.UTC expects 0–11
      get('day'),
      get('hour'),
      get('minute'),
      get('second')
    )
  );
};

// ── Date helpers ──────────────────────────────────────────────────────────────

// DCO business rule: no NDD on Sundays (bank holidays handled via excludedDates)
const EXCLUDED_DAYS = ['sunday'];

/**
 * Ordinal suffix — handles 11th/12th/13th correctly via range check.
 * Adapted from a reference implementation used for similar clients.
 */
const nthSuffix = (n) => (n > 3 && n < 21 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');

/**
 * Day name in lowercase using the Intl API.
 * timeZone: 'UTC' is correct here because our delivery dates are
 * stored as UTC-midnight objects with the UK date already baked in.
 */
const getDayName = (date) => date.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'UTC' }).toLowerCase();

const formatDeliveryDate = (date) => {
  const day = date.getUTCDate();
  const fmt = (opts) => date.toLocaleDateString('en-GB', { timeZone: 'UTC', ...opts });
  return `${fmt({ weekday: 'long' })} ${day}${nthSuffix(day)} ${fmt({ month: 'long' })}`;
};

const toISODate = (date) => date.toISOString().split('T')[0];

/** Returns true if the date is neither an excluded weekday nor a blackout date. */
const isDateAllowed = (date, excludedDays, excludedDates) =>
  !excludedDays.includes(getDayName(date)) && !excludedDates.includes(toISODate(date));

const addDays = (date, n) => {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + n);
  return d;
};

/** Advance a date until `isDateAllowed` returns true. */
const skipExcluded = (date, excludedDays, excludedDates) => {
  let guard = 0;
  while (!isDateAllowed(date, excludedDays, excludedDates) && guard++ < 60) {
    date = addDays(date, 1);
  }
  return date;
};

// ── DCO delivery date calculation ─────────────────────────────────────────────

/**
 * Builds a Date representing today's cutoff time in the same UK-local coordinate
 * system as ukNow. Cloning ukNow preserves the correct year/month/day;
 * setUTCHours then snaps the time to the cutoff (zeroing seconds + ms).
 */
const cutoffToday = (ukNow, cutoffStr) => {
  const [h = 14, m = 0] = (cutoffStr ?? '14:00').split(':').map(Number);
  const d = new Date(ukNow);
  d.setUTCHours(h, m, 0, 0);
  return d;
};

const isAfterCutoff = (ukNow, cutoffStr) => ukNow >= cutoffToday(ukNow, cutoffStr);

/**
 * Calculate the next delivery date using DCO dispatch rules.
 *
 * @param {Date}     ukNow         – current UK time (UTC fields = local UK)
 * @param {string}   cutoffStr     – cutoff as "HH:MM" (e.g. "14:00")
 * @param {string[]} excludedDates – ISO dates to skip (bank holidays etc.)
 * @returns {Date}
 */
const getDeliveryDate = (ukNow, cutoffStr, excludedDates) => {
  const day = ukNow.getUTCDay();
  const after = isAfterCutoff(ukNow, cutoffStr);

  // Midnight of today in UTC (UK date already baked in via getUKNow)
  const today = new Date(Date.UTC(ukNow.getUTCFullYear(), ukNow.getUTCMonth(), ukNow.getUTCDate()));

  // prettier-ignore
  const dispatchOffset =
    day === 0 ? 1 :               // Sun → Mon
      day === 6 ? 2 :               // Sat → Mon
        day === 5 ? (after ? 3 : 0) : // Fri: after cutoff → Mon (+3), before → Fri (+0)
          after ? 1 : 0; // Mon–Thu

  const dispatch = skipExcluded(addDays(today, dispatchOffset), EXCLUDED_DAYS, excludedDates);
  const delivery = skipExcluded(addDays(dispatch, 1), EXCLUDED_DAYS, excludedDates);

  return delivery;
};

/**
 * Seconds remaining until today's cutoff (UK time).
 * Returns `null` on weekends (no cutoff — show static delivery message).
 * Returns `0`    when the cutoff has already passed today.
 */
const secondsToCutoff = (ukNow, cutoffStr) => {
  if (ukNow.getUTCDay() === 0 || ukNow.getUTCDay() === 6) return null;
  return Math.max(0, Math.floor((cutoffToday(ukNow, cutoffStr) - ukNow) / 1_000));
};

// ── Dropship detection ────────────────────────────────────────────────────────

/**
 * Returns `true` if the variant's shipping group should be treated as dropship.
 * Two-stage check:
 *  1. Auto-pattern: any group containing "dropship" (case-insensitive) is excluded
 *     automatically — new suppliers following that naming convention need no config change.
 *  2. Manual exclusion list: catches edge cases with non-obvious names (e.g. Holden-DS).
 * An empty / missing group is treated as NDD-eligible (safe default).
 */
const isDropship = (shippingGroup, excludedGroups) => {
  if (!shippingGroup) return false;

  if (shippingGroup.toLowerCase().includes('dropship')) return true;

  const excluded = excludedGroups?.map((g) => g.trim().toLowerCase()).filter(Boolean) ?? [];

  return excluded.includes(shippingGroup.trim().toLowerCase());
};

// ── DOM rendering ─────────────────────────────────────────────────────────────

const pad2 = (n) => String(Math.floor(n)).padStart(2, '0');

const renderCountdown = (fields, seconds, deliveryDate, strings) => {
  const h = Math.floor(seconds / 3_600);
  const m = Math.floor((seconds % 3_600) / 60);
  const s = seconds % 60;
  updateNode(fields.prefix, `${strings.orderInNext}`);
  updateNode(fields.timer, `${h}\u2009h\u00a0${pad2(m)}\u2009m\u00a0${pad2(s)}\u2009s`);
  updateNode(fields.suffix, `${strings.deliveredBy}`);
  updateNode(fields.date, formatDeliveryDate(deliveryDate));
  updateNode(fields.dropship, '', true);
};

const renderWeekend = (fields, deliveryDate, strings) => {
  updateNode(fields.prefix, `${strings.orderNow}`);
  updateNode(fields.timer, '');
  updateNode(fields.suffix, '');
  updateNode(fields.date, formatDeliveryDate(deliveryDate));
  updateNode(fields.dropship, '', true);
};

/**
 * Dropship — renders the richtext HTML message directly.
 * Uses innerHTML because the message may contain inline links authored by the editor.
 */
const renderDropship = (fields, messageHtml) => {
  updateNode(fields.prefix, '');
  updateNode(fields.timer, '');
  updateNode(fields.suffix, '');
  updateNode(fields.date, '');
  updateNode(fields.dropship, messageHtml, true);
};
const updateNode = (node, content, isHTML = false) => {
  if (!node || (isHTML ? node.innerHTML === content : node.textContent === content)) return;
  isHTML ? (node.innerHTML = content) : (node.textContent = content);
};
// ── Custom Element ────────────────────────────────────────────────────────────

class NddCountdown extends HTMLElement {
  #fields = null; // cached DOM refs: { prompt, prefix, timer, suffix, date, link }
  #config = null;
  #ticker = null;
  #unsubscribeVariant = null;
  #lastVariantId = '';
  #testStartReal = null;
  #testStartParsed = null;

  connectedCallback() {
    this.#fields = {
      prompt: this.querySelector('.ndd-countdown__prompt'),
      prefix: this.querySelector('.ndd-countdown__prefix'),
      timer: this.querySelector('.ndd-countdown__time'),
      suffix: this.querySelector('.ndd-countdown__suffix'),
      date: this.querySelector('.ndd-countdown__date'),
      dropship: this.querySelector('.ndd-countdown__dropship')
    };
    this.#config = this.#parseConfig();

    if (!this.#config || !this.#fields.prompt) return;

    this.#lastVariantId = this.#getSelectedVariantId();

    // Capture real clock + parsed test time so the countdown ticks forward
    // from the configured test datetime rather than staying frozen.
    if (this.#config.testMode && this.#config.testDatetime) {
      const td = this.#config.testDatetime;
      const withSeconds = td.length === 16 ? `${td}:00Z` : `${td}Z`;
      const parsed = new Date(withSeconds);
      if (!isNaN(parsed.getTime())) {
        this.#testStartReal = Date.now();
        this.#testStartParsed = parsed.getTime();
      }
    }

    this.#update();

    // Tick every second — countdown display and post-cutoff hiding
    this.#ticker = setInterval(() => this.#update(), 1_000);

    // Variant change detection — theme pub/sub (published by VariantSelects CE in global.js)
    // Falls back gracefully if pubsub.js has not loaded (e.g. theme editor preview).
    if (typeof subscribe === 'function') {
      this.#unsubscribeVariant = subscribe('variant-change', ({ data }) => {
        // Filter to this section only — avoids reacting to variant changes on
        // other product sections that may exist on the same page.
        if (data?.sectionId && data.sectionId !== this.dataset.sectionId) return;

        const newId = data?.variant?.id?.toString() ?? '';
        if (newId && newId !== this.#lastVariantId) {
          this.#lastVariantId = newId;
          this.#update();
        }
      });
    }
  }

  disconnectedCallback() {
    clearInterval(this.#ticker);
    this.#unsubscribeVariant?.();
  }

  #parseConfig() {
    const scriptEl = this.querySelector('script[type="application/json"]');
    if (!scriptEl) return null;
    try {
      return JSON.parse(scriptEl.textContent);
    } catch (err) {
      console.warn('[NDD] Failed to parse config:', err);
      return null;
    }
  }

  #getSelectedVariantId() {
    return new URLSearchParams(window.location.search).get('variant') ?? this.dataset.variantId ?? '';
  }

  #getShippingGroup(variantId) {
    return this.#config.variantGroups?.[String(variantId)] ?? '';
  }

  #isNddUnavailable(variantId) {
    return this.#config.variantNddUnavailable?.[String(variantId)] === true;
  }

  #update() {
    const {
      excludedDates = [],
      isTrade = false,
      nddExcludedGroups = [],
      cutoffRetail = '14:00',
      cutoffTrade = '15:00',
      dropshipMessage = '',
      testMode = false,
      testDatetime = '',
      strings = {}
    } = this.#config;

    const cutoff = isTrade ? cutoffTrade : cutoffRetail;
    const variantId = this.#getSelectedVariantId();
    const shippingGroup = this.#getShippingGroup(variantId);

    // Explicit NDD Unavailable metafield — overrides shipping group logic
    if (this.#isNddUnavailable(variantId)) {
      this.#fields.prompt.classList.add('is-visible');
      renderDropship(this.#fields, dropshipMessage);
      return;
    }

    // Shipping group in NDD-excluded list — treat as dropship
    if (shippingGroup && isDropship(shippingGroup, nddExcludedGroups)) {
      this.#fields.prompt.classList.add('is-visible');
      renderDropship(this.#fields, dropshipMessage);
      return;
    }

    // In test mode, advance the simulated clock by real elapsed time so the
    // countdown actually ticks rather than displaying a frozen value.
    const testOffset = testMode && this.#testStartReal !== null ? Date.now() - this.#testStartReal : 0;

    const ukNow = getUKNow(testMode, testDatetime, testOffset);
    const secs = secondsToCutoff(ukNow, cutoff);

    // Weekend — no fixed cutoff; show static delivery date
    if (secs === null) {
      this.#fields.prompt.classList.add('is-visible');
      renderWeekend(this.#fields, getDeliveryDate(ukNow, cutoff, excludedDates), strings);
      return;
    }

    // After cutoff — show next available delivery date
    if (secs === 0) {
      this.#fields.prompt.classList.add('is-visible');
      renderWeekend(this.#fields, getDeliveryDate(ukNow, cutoff, excludedDates), strings);
      return;
    }

    // Active countdown
    this.#fields.prompt.classList.add('is-visible');
    renderCountdown(this.#fields, secs, getDeliveryDate(ukNow, cutoff, excludedDates), strings);
  }
}

if (!customElements.get('ndd-countdown')) {
  customElements.define('ndd-countdown', NddCountdown);
}
